// src/components/TruckSelectorModal.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Truck,
  Navigation,
  MapPin,
  Calendar,
  Clock,
  Ban,
  Timer,
  AlertTriangle,
  Search,
  X, 
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import axios from "../axiosConfig";
import "../styles/TruckSelectorModal.css";
import { useTranslation } from "react-i18next";

function TruckSelectorModal({
  isOpen,
  onClose,
  onSelect,
  selectedType,
  selectedPickupDelivery,
  currentLoad,
  title,
  setIsTruckUpdating,
  onTruckAssigned,
  mode = "single",
  allPickups = [],
  allDeliveries = [],
}) {
  const { t, i18n } = useTranslation("truck_selector_modal");

  const locale = useMemo(() => {
    const lng = (i18n.language || "en").toLowerCase();
    if (lng.startsWith("pt")) return "pt-PT";
    if (lng.startsWith("es")) return "es-ES";
    if (lng.startsWith("fr")) return "fr-FR";
    return "en-GB";
  }, [i18n.language]);

  const quickMode = currentLoad?.isExternalCarrier === true;

  console.log("TruckSelectorModal currentLoad:", currentLoad);
  console.log("carrierId:", currentLoad?.carrierId, "quickMode:", quickMode);

  const [manualPlate, setManualPlate] = useState("");
  const [manualPlateError, setManualPlateError] = useState("");

  const [availableTrucks, setAvailableTrucks] = useState([]);
  const [alsoAssignToDeparture, setAlsoAssignToDeparture] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [assignedOverride, setAssignedOverride] = useState(null);
  const [sessionNumberById, setSessionNumberById] = useState({});
  const requestedSessionNumbersRef = useRef(new Set());

  // dados “frescos” para o modal (server ou optimistic)
  const [freshSelected, setFreshSelected] = useState(null);
  const [freshPickups, setFreshPickups] = useState([]);
  const [freshDeliveries, setFreshDeliveries] = useState([]);

  const [loadingTrucks, setLoadingTrucks] = useState(false);

  const [truckSearch, setTruckSearch] = useState("");
  const [onlyAvailableTrucks, setOnlyAvailableTrucks] = useState(false);
  const [selectedTruckId, setSelectedTruckId] = useState(null);
  const [isSavingTruck, setIsSavingTruck] = useState(false);
  const [truckConfirmError, setTruckConfirmError] = useState("");

  // ===== "fun loader" (5 checkpoints) =====
  const LOADING_STEPS = 5;
  const [prepStep, setPrepStep] = useState(0); // 0..4
  const [showPrep, setShowPrep] = useState(false);

  const getConflictSessionId = (block) =>
    block?.transportsessionId ?? block?.sessionId ?? null;
  const getSessionNumberLabel = (block) => {
    const sessionId = getConflictSessionId(block);
    if (sessionId == null) return null;
    const number = sessionNumberById[String(sessionId)];
    if (number === undefined) return "A consultar...";
    if (number === null) return "Número indisponível";
    return `#${number}`;
  };
  const loadCompanySessionNumber = (block) => {
    const sessionId = getConflictSessionId(block);
    if (sessionId == null) return;
    const key = String(sessionId);
    if (requestedSessionNumbersRef.current.has(key)) return;
    requestedSessionNumbersRef.current.add(key);
    axios.get(`/transportsessions/${encodeURIComponent(key)}`)
      .then(({ data }) => {
        const number = data?.transportSessionNumber;
        setSessionNumberById((prev) => ({
          ...prev,
          [key]: number != null && String(number).trim() !== ""
            ? String(number)
            : null,
        }));
      })
      .catch((error) => {
        console.warn("[TruckSelectorModal] Session number unavailable:", error);
        setSessionNumberById((prev) => ({ ...prev, [key]: null }));
      });
  };
  const toggleExpand = (id, e, conflict = null) => {
    e?.stopPropagation?.();
    const blocks = conflict?.blocks ?? (conflict?.block ? [conflict.block] : []);
    blocks.forEach(loadCompanySessionNumber);
    setExpanded((prev) => (prev.has(id) ? new Set() : new Set([id])));
  };

  const trPointType = (rawType) => {
    const key = `pointTypes.${String(rawType || "").toUpperCase()}`;
    const translated = t(key, { defaultValue: "" });
    return translated || rawType || "-";
  };

  const fmtDateTime = (d) => {
    try {
      return d.toLocaleString(locale, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return d.toString();
    }
  };

  /* ---------- override persistido por contexto ---------- */
  const STORAGE_PREFIX = "tsm:truckOverride:";
  const getCtxKey = () =>
    mode === "single"
      ? `${STORAGE_PREFIX}pd:${selectedPickupDelivery?.id ?? "na"}`
      : `${STORAGE_PREFIX}load:${currentLoad?.id ?? "na"}:${selectedType}`;

  const persistOverride = (truckId) => {
    try {
      sessionStorage.setItem(getCtxKey(), String(truckId));
    } catch {}
  };

  const readOverride = () => {
    try {
      return sessionStorage.getItem(getCtxKey());
    } catch {
      return null;
    }
  };

  const clearOverride = () => {
    try {
      sessionStorage.removeItem(getCtxKey());
    } catch {}
  };

  /* ---------- reset leve quando muda o contexto ---------- */
  useEffect(() => {
    setAssignedOverride(null);
    setExpanded(new Set());
    setSelectedTruckId(null);
    setAlsoAssignToDeparture(false);
    setTruckSearch("");
    setOnlyAvailableTrucks(false);
    setTruckConfirmError("");
  }, [isOpen, currentLoad?.id, mode, selectedType, selectedPickupDelivery?.id]);

  /* ---------- ao abrir: aplica override + fetch “sem cache” ---------- */
  useEffect(() => {
    if (!isOpen) return;
    setExpanded(new Set());

    // ✅ QUICK MODE: no overrides, no fetches — just reset manual input
    if (quickMode) {
      setManualPlate("");
      setManualPlateError("");
      return;
    }

    // aplica override guardado (optimistic imediato)
    const ov = readOverride();
    if (ov != null) {
      const ovId = Number.isNaN(Number(ov)) ? ov : Number(ov);
      setAssignedOverride(ovId);

      if (mode === "single") {
        setFreshSelected((prev) =>
          applyOptimisticToPoint(prev ?? selectedPickupDelivery, ovId)
        );
      } else if (selectedType === "pickup") {
        setFreshPickups((prev) => {
          const src = prev?.length ? prev : allPickups;
          return src.map((p) => ({ ...p, truckId: ovId }));
        });
      } else {
        setFreshDeliveries((prev) => {
          const src = prev?.length ? prev : allDeliveries;
          return src.map((p) => ({ ...p, truckId: ovId }));
        });
      }
    }

    // força fresh do backend
    const noCache = { params: { _: Date.now() } };

    if (mode === "single" && selectedPickupDelivery?.id) {
      axios
        .get(`/pickupdeliveries/by-id/${selectedPickupDelivery.id}`, noCache)
        .then((res) => {
          setFreshSelected(res.data);
          clearOverride();
          setAssignedOverride(null);
        })
        .catch((err) =>
          console.error("Error fetching selected pickup/delivery", err)
        );
    } else if (mode === "bulk" && currentLoad?.id) {
      axios
        .get(`/loads/${currentLoad.id}/points`, noCache)
        .then((res) => {
          setFreshPickups(res.data?.pickups ?? []);
          setFreshDeliveries(res.data?.deliveries ?? []);
          clearOverride();
          setAssignedOverride(null);
        })
        .catch((err) => console.error("Error fetching load points", err));
    }
  }, [
    isOpen,
    quickMode,
    mode,
    selectedPickupDelivery?.id,
    currentLoad?.id,
    selectedType,
    allPickups,
    allDeliveries,
  ]);

  /* ---------- trucks + blocking (sem cache) ---------- */
  useEffect(() => {
    if (!isOpen) return;
    // ✅ QUICK MODE: no fetching / no blocking calculations
    if (quickMode) return;

    // figure out which session this modal belongs to
    const sid =
      mode === "single"
        ? selectedPickupDelivery?.transportsessionId ??
          currentLoad?.transportsessionId ??
          null
        : currentLoad?.transportsessionId ?? null;

    const noCache = { params: { _: Date.now() } };

    // ✅ Only relax blocks for SAME session in SINGLE mode (like DriverSelectorModal)
    const blockingParams =
      sid && mode === "single"
        ? { params: { _: Date.now(), originSessionId: sid } }
        : noCache;

    setLoadingTrucks(true);

    Promise.all([
      axios.get("/trucks/with-latest-assignments", noCache),
      axios.get("/pickupdeliveries/truck-blocking", blockingParams),
    ])
      .then(([trucksRes, blocksRes]) => {
        const blockingData = blocksRes.data || [];
        const trucksWithBlocking = (trucksRes.data || []).map((truck) => {
          const blockEntry = blockingData.find((b) => b.truckId === truck.id);
          return { ...truck, sessionBlocks: blockEntry?.sessionBlocks || [] };
        });
        setAvailableTrucks(trucksWithBlocking);
      })
      .catch((err) => console.error("Error fetching truck data", err))
      .finally(() => setLoadingTrucks(false));
  }, [
    isOpen,
    quickMode,
    mode,
    selectedPickupDelivery?.transportsessionId,
    currentLoad?.transportsessionId,
  ]);

  // ===== Fun loader logic: advance point-to-point (no back-and-forth) =====
  useEffect(() => {
    if (!isOpen) return;

    // quick mode doesn't fetch trucks -> no loader needed
    if (quickMode) {
      setShowPrep(false);
      setPrepStep(0);
      return;
    }

    let tickTimer = null;
    let doneTimer = null;

    if (loadingTrucks) {
      setShowPrep(true);
      setPrepStep(0);

      // move to step 1..3 quickly, then "hold" at step 3 until fetch finishes
      let s = 0;
      tickTimer = setInterval(() => {
        s += 1;
        setPrepStep((prev) => {
          const next = Math.min(3, Math.max(prev, s));
          return next;
        });
        if (s >= 3 && tickTimer) {
          clearInterval(tickTimer);
          tickTimer = null;
        }
      }, 350);
    } else {
      // if we were showing prep, finish to step 4, show "ready" briefly, then hide
      if (showPrep) {
        setPrepStep(4);
        doneTimer = setTimeout(() => {
          setShowPrep(false);
          setPrepStep(0);
        }, 650);
      }
    }

    return () => {
      if (tickTimer) clearInterval(tickTimer);
      if (doneTimer) clearTimeout(doneTimer);
    };
  }, [isOpen, quickMode, loadingTrucks, showPrep]);

  if (!isOpen) return null;

  /* ================= helpers de lógica ================= */
  function applyOptimisticToPoint(basePoint, truckId) {
    if (!basePoint) return basePoint;
    if (basePoint.truckRole === "arrival")
      return { ...basePoint, truckIdArrival: truckId };
    if (basePoint.truckRole === "departure")
      return { ...basePoint, truckIdDeparture: truckId };
    return { ...basePoint, truckId };
  }

  function getUniformAssignedTruckId(points = []) {
    const ids = points
      .map((p) => p?.truckId ?? null)
      .filter((id) => id != null);
    if (ids.length === 0) return null;
    return ids.every((id) => String(id) === String(ids[0])) ? ids[0] : null;
  }

  function getAssignedTruckIdForPoint(point) {
    if (!point) return null;
    if (point.truckRole === "arrival") return point.truckIdArrival ?? null;
    if (point.truckRole === "departure") return point.truckIdDeparture ?? null;
    return point.truckId ?? null;
  }

  function isTruckAlreadyAssigned(truck, selectedPoint) {
    if (!truck || !selectedPoint) return false;
    const role = selectedPoint.truckRole;
    if (role === "arrival") return selectedPoint.truckIdArrival === truck.id;
    if (role === "departure") return selectedPoint.truckIdDeparture === truck.id;
    return selectedPoint.truckId === truck.id;
  }

  function mergePoint(base, fresh) {
    if (!fresh) return base;
    if (!base) return fresh;
    const pick = (nv, ov) =>
      nv !== undefined && nv !== null && nv !== "" ? nv : ov;
    return {
      ...base,
      ...fresh,
      date: pick(fresh.date, base.date),
      hour: pick(fresh.hour, base.hour),
      type: pick(fresh.type, base.type),
      location: pick(fresh.location, base.location),
      transportsessionId: pick(fresh.transportsessionId, base.transportsessionId),
      truckId: pick(fresh.truckId, base.truckId),
      truckIdArrival: pick(fresh.truckIdArrival, base.truckIdArrival),
      truckIdDeparture: pick(fresh.truckIdDeparture, base.truckIdDeparture),
      truckRole: pick(fresh.truckRole, base.truckRole),
    };
  }

  function isTruckBlockedForCurrent(truck, selectedPoint) {
    if (!truck?.sessionBlocks?.length || !selectedPoint) return null;
    const sessionId = selectedPoint.transportsessionId ?? null;
    if (!selectedPoint.date || !selectedPoint.hour) return null;

    const pointStart = new Date(
      `${selectedPoint.date}T${String(selectedPoint.hour).padStart(5, "0")}:00`
    );
    const pointEnd = new Date(pointStart.getTime() + 45 * 60 * 1000);

    const isSamePoint = (p, sp) =>
      p &&
      sp &&
      p.date === sp.date &&
      p.hour === sp.hour &&
      p.type === sp.type &&
      (p.location || "") === (sp.location || "");

    for (const block of truck.sessionBlocks) {
      const start = new Date(block.start);
      const end = new Date(block.end);
      const blockSessionId = block.transportsessionId ?? block.sessionId ?? null;
      const isSameSession =
        blockSessionId != null &&
        sessionId != null &&
        String(blockSessionId) === String(sessionId);

      if (mode === "single" && isSameSession) continue;

      if (
        block.blockType === "FULL_SESSION" ||
        block.blockType === "LOAD_FULL" ||
        block.blockType === "LOAD_HALF"
      ) {
        const overlaps = pointStart < end && start < pointEnd;
        if (overlaps) {
          return {
            isBlocked: true,
            block: {
              transportsessionId: blockSessionId,
              loadId: block.loadId ?? null,
              blockType: block.blockType,
              start,
              end,
              points: block.points || [],
            },
          };
        }
        continue;
      }

      if (
        block.blockType === "POINT" &&
        Array.isArray(block.points) &&
        block.points.length
      ) {
        for (const p of block.points) {
          const pSessionId = block.transportsessionId ?? block.sessionId ?? null;
          const isSameSessionPoint =
            pSessionId != null &&
            sessionId != null &&
            String(pSessionId) === String(sessionId);

          if (mode === "single" && isSameSessionPoint) continue;
          if (isSamePoint(p, selectedPoint)) continue;
          if (!p.date || !p.hour) continue;

          const pStart = new Date(`${p.date}T${String(p.hour).padStart(5, "0")}:00`);
          const pEnd = new Date(pStart.getTime() + 45 * 60 * 1000);

          const overlaps = pointStart < pEnd && pStart < pointEnd;
          if (overlaps) {
            return {
              isBlocked: true,
              block: {
                transportsessionId: pSessionId,
                loadId: block.loadId ?? null,
                blockType: "POINT",
                start: pStart,
                end: pEnd,
                points: [p],
              },
            };
          }
        }
      }
    }
    return null;
  }

  function isTruckBlockedForBulk(truck, allPickupsArr = [], allDeliveriesArr = []) {
    if (!truck.sessionBlocks?.length) return null;
    const relevantPoints = selectedType === "pickup" ? allPickupsArr : allDeliveriesArr;
    const allPoints = relevantPoints.filter((p) => p?.date && p?.hour);
    const result = [];

    for (const point of allPoints) {
      const pointTime = new Date(`${point.date}T${point.hour}:00`);
      const pointStart = pointTime;
      const pointEnd = new Date(pointTime.getTime() + 45 * 60 * 1000);

      for (const block of truck.sessionBlocks) {
        if (
          mode === "bulk" &&
          block.transportsessionId &&
          currentLoad?.transportsessionId &&
          String(block.transportsessionId) === String(currentLoad.transportsessionId)
        ) {
          continue;
        }

        const start = new Date(block.start);
        const end = new Date(block.end);
        const isOverlap = pointStart < end && start < pointEnd;

        if (isOverlap) {
          const existing = result.find(
            (b) =>
              b.blockType === block.blockType &&
              (block.blockType === "LOAD_FULL"
                ? b.loadId === block.loadId
                : b.sessionId === block.transportsessionId)
          );

          const cause = {
            type: point.type,
            location: point.location,
            date: point.date,
            hour: point.hour,
          };

          if (existing) {
            existing.causedBy.push(cause);
            existing.causedBy.sort(
              (a, b) =>
                new Date(`${a.date}T${a.hour}:00`) - new Date(`${b.date}T${b.hour}:00`)
            );
          } else {
            result.push({
              sessionId: block.transportsessionId,
              loadId: block.loadId ?? null,
              blockType: block.blockType,
              start,
              end,
              points: block.points || [],
              causedBy: [cause],
            });
          }
        }
      }
    }

    if (result.length === 0) return null;

    const seen = new Set();
    const dedup = [];
    for (const block of result) {
      let key;
      if (block.blockType === "FULL_SESSION") key = `FULL_SESSION-${block.sessionId}`;
      else if (block.blockType === "LOAD_FULL" || block.blockType === "LOAD_HALF")
        key = `${block.blockType}-${block.loadId}`;
      else if (block.blockType === "POINT") key = `POINT-${block.loadId}-${block.start.toISOString()}`;
      if (!seen.has(key)) {
        seen.add(key);
        dedup.push(block);
      }
    }

    const hasFullSession = dedup.some((b) => b.blockType === "FULL_SESSION");
    const hasFullLoad = dedup.some((b) => b.blockType === "LOAD_FULL");
    let filtered = dedup;
    if (hasFullSession) filtered = dedup.filter((b) => b.blockType === "FULL_SESSION");
    else if (hasFullLoad)
      filtered = dedup.filter((b) => b.blockType === "LOAD_FULL" || b.blockType === "LOAD_HALF");

    return { isBlocked: filtered.length > 0, blocks: filtered };
  }

  // --- Título dinâmico (sem LOAD ID no H2) ---
  const providedTitle = (title ?? "").trim();
  const sanitizedProvidedTitle = providedTitle.replace(/\s*\(LOAD\s*#\s*[\w-]+\)\s*$/i, "");

  const baseTitle =
    sanitizedProvidedTitle ||
    (mode === "bulk"
      ? selectedType === "pickup"
        ? t("titles.bulk.pickups")
        : t("titles.bulk.deliveries")
      : (() => {
          const tp = selectedPickupDelivery?.type;
          if (tp === "PICKUP") return t("titles.single.pickup");
          if (tp === "DELIVERY") return t("titles.single.delivery");
          if (tp === "STRATEGICPOINT") {
            return selectedPickupDelivery?.truckRole === "arrival"
              ? t("titles.single.arrival")
              : selectedPickupDelivery?.truckRole === "departure"
              ? t("titles.single.departure")
              : t("titles.single.strategicPoint");
          }
          return t("titles.single.default");
        })());

  const computedTitle = baseTitle;

  const handleQuickAssign = async () => {
    const plate = String(manualPlate || "").trim().toUpperCase();
    if (!plate) {
      setManualPlateError(
        t("quickMode.errors.required", { defaultValue: "❌ Truck plate is required." })
      );
      return;
    }

    setManualPlateError("");
    setIsTruckUpdating?.(true);

    try {
      if (mode === "single" && selectedPickupDelivery?.id) {
        const pdId = selectedPickupDelivery.id;
        const isSP = selectedPickupDelivery.type === "STRATEGICPOINT";
        const role = selectedPickupDelivery.truckRole;

        if (isSP && role === "arrival") {
          await axios.put(`/pickupdeliveries/${pdId}/assign-truck-arrival`, {
            truckId: null,
            truckPlate: plate,
          });
          if (alsoAssignToDeparture) {
            await axios.put(`/pickupdeliveries/${pdId}/assign-truck-departure`, {
              truckId: null,
              truckPlate: plate,
            });
          }
        } else if (isSP && role === "departure") {
          await axios.put(`/pickupdeliveries/${pdId}/assign-truck-departure`, {
            truckId: null,
            truckPlate: plate,
          });
          if (alsoAssignToDeparture) {
            await axios.put(`/pickupdeliveries/${pdId}/assign-truck-arrival`, {
              truckId: null,
              truckPlate: plate,
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pdId}/assign-truck`, {
            truckId: null,
            truckPlate: plate,
            type: selectedType,
          });
        }
      }

      if (mode === "bulk" && currentLoad?.id) {
        const url =
          selectedType === "pickup"
            ? "/pickupdeliveries/assign-truck-plate-to-all-pickups"
            : "/pickupdeliveries/assign-truck-plate-to-all-deliveries";
        await axios.put(url, { loadId: currentLoad.id, truckPlate: plate });
      }

      onTruckAssigned?.({ truckId: null, plate, isManual: true });

      // ❌ não chamar onSelect no quick mode
      onClose();
    } catch (e) {
      console.error("❌ Quick assign failed:", e);
      setManualPlateError(
        t("quickMode.errors.failed", { defaultValue: "❌ Failed to assign this truck plate." })
      );
    } finally {
      setIsTruckUpdating?.(false);
    }
  };

  const contextPoint =
    mode === "single"
      ? mergePoint(selectedPickupDelivery, freshSelected)
      : selectedType === "pickup"
        ? (freshPickups[0] ?? allPickups[0])
        : (freshDeliveries[0] ?? allDeliveries[0]);

  const contextType = String(
    contextPoint?.type || selectedType || ""
  ).toUpperCase();

  const contextLabel =
    contextType === "PICKUP"
      ? t("pointTypes.PICKUP", { defaultValue: "Pick-up" })
      : contextType === "DELIVERY"
        ? t("pointTypes.DELIVERY", { defaultValue: "Delivery" })
        : t("titles.single.strategicPoint", {
            defaultValue: "Strategic point",
          });

  // Company-facing Load number, never use the internal ID as fallback.
  const companyLoadNumber =
    currentLoad?.loadNumber ??
    contextPoint?.load?.loadNumber ??
    null;

  const displayDate = (raw) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(
      String(raw ?? "")
    );

    return match
      ? `${match[3]}/${match[2]}/${match[1]}`
      : "—";
  };

  const displayTime = (point) => {
    const start = String(point?.hour || "").slice(0, 5);
    const end = String(point?.hourEnd || "").slice(0, 5);

    return String(point?.scheduleType || "").toUpperCase() === "WINDOW" &&
      start &&
      end
      ? `${start}–${end}`
      : start || "—";
  };

  const effectivePoint = mergePoint(
    selectedPickupDelivery,
    freshSelected
  );

  const effectivePickups =
    freshPickups.length ? freshPickups : allPickups;

  const effectiveDeliveries =
    freshDeliveries.length ? freshDeliveries : allDeliveries;

  // Reuse the existing operational blocking rules.
  const truckBlocking = (truck) =>
    mode === "bulk"
      ? isTruckBlockedForBulk(
          truck,
          effectivePickups,
          effectiveDeliveries
        )
      : isTruckBlockedForCurrent(truck, effectivePoint);

  const currentAssignedTruckId =
    assignedOverride ??
    (mode === "single"
      ? getAssignedTruckIdForPoint(effectivePoint)
      : getUniformAssignedTruckId(
          selectedType === "pickup"
            ? effectivePickups
            : effectiveDeliveries
        ));

  const isCurrentTruck = (truck) =>
    currentAssignedTruckId != null &&
    String(currentAssignedTruckId) === String(truck.id);

  const isTruckUnavailable = (truck) =>
    !!truckBlocking(truck)?.isBlocked ||
    isCurrentTruck(truck);

  const selectableTruckCount = availableTrucks.filter(
    (truck) => !isTruckUnavailable(truck)
  ).length;

  const selectedTruck = availableTrucks.find(
    (truck) =>
      selectedTruckId != null &&
      String(truck.id) === String(selectedTruckId)
  );

  const confirmTruckSelection = async () => {
    if (
      !selectedTruck ||
      isSavingTruck ||
      loadingTrucks ||
      showPrep ||
      isTruckUnavailable(selectedTruck)
    ) {
      return;
    }

    setIsSavingTruck(true);
    setTruckConfirmError("");
    setIsTruckUpdating?.(true);

    try {
      // Same existing assignment callback.
      const result = await onSelect(
        selectedTruck,
        selectedType,
        alsoAssignToDeparture
      );

      if (result === false) {
        setTruckConfirmError(
          t("premium.assignmentFailed", {
            defaultValue: "Assignment was not confirmed.",
          })
        );
        return;
      }

      setAssignedOverride(selectedTruck.id);
      persistOverride(selectedTruck.id);

      onClose();
    } catch (error) {
      console.error(
        "[TruckSelectorModal] Assignment failed",
        error
      );

      setTruckConfirmError(
        t("premium.assignmentFailed", {
          defaultValue: "Assignment failed. Please try again.",
        })
      );
    } finally {
      setIsTruckUpdating?.(false);
      setIsSavingTruck(false);
    }
  };

  return (
    <div
      className="truck-modal-overlay"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !isSavingTruck
        ) {
          onClose();
        }
      }}
    >
      <div className="truck-modal tsm-premium">

        <aside className="tsm-premium-side">

          <div className="tsm-premium-side-intro">
            <span className="tsm-premium-side-icon">
              <Truck size={25} />
            </span>

            <span>
              {t("premium.assignTruckTo", {
                defaultValue: "ASSIGN TRUCK TO",
              })}
            </span>
          </div>

          <h2 className="tsm-premium-point-title">
            {mode === "bulk"
              ? selectedType === "pickup"
                ? t("premium.allPickups", {
                    defaultValue: "ALL PICK-UPS",
                  })
                : t("premium.allDeliveries", {
                    defaultValue: "ALL DELIVERIES",
                  })
              : `${contextLabel}${
                  contextPoint?.id != null
                    ? ` #${contextPoint.id}`
                    : ""
                }`}
          </h2>

          {companyLoadNumber != null &&
            String(companyLoadNumber).trim() !== "" && (
              <span className="tsm-premium-load-tag">
                LOAD #{companyLoadNumber}
              </span>
            )}

          <div
            className="tsm-premium-hero"
            aria-hidden="true"
          />

          <div className="tsm-premium-context">

            <div className="tsm-premium-context-line">
              <MapPin size={19} />
              <div>
                <small>
                  {t("premium.location", {
                    defaultValue: "Location",
                  })}
                </small>
                <strong>
                  {contextPoint?.location || "—"}
                </strong>
                <span>
                  {[
                    contextPoint?.city ??
                      contextPoint?.place?.city,
                    contextPoint?.country ??
                      contextPoint?.place?.country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            </div>

            <div className="tsm-premium-context-line">
              <Calendar size={19} />
              <div>
                <small>
                  {t("premium.date", {
                    defaultValue: "Date",
                  })}
                </small>
                <strong>
                  {displayDate(contextPoint?.date)}
                </strong>
              </div>
            </div>

            <div className="tsm-premium-context-line">
              <Clock size={19} />
              <div>
                <small>
                  {t("premium.schedule", {
                    defaultValue: "Schedule",
                  })}
                </small>
                <strong>
                  {displayTime(contextPoint)}
                </strong>
              </div>
            </div>

          </div>

          <div className="tsm-premium-route-card">
            <svg
              viewBox="0 0 310 44"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                d="M12 31 C84 -6 139 64 226 22 S284 23 299 15"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeDasharray="5 5"
              />
              <circle
                cx="12"
                cy="31"
                r="5"
                fill="currentColor"
              />
              <circle
                cx="299"
                cy="15"
                r="6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>

            <div className="tsm-premium-route-summary">
              <strong>
                {contextPoint?.location || "—"}
              </strong>
              <span>
                {mode === "bulk"
                  ? `${
                      selectedType === "pickup"
                        ? effectivePickups.length
                        : effectiveDeliveries.length
                    } stops`
                  : contextLabel}
              </span>
            </div>
          </div>

        </aside>

        <section className="tsm-premium-main">

          <header className="tsm-premium-heading">
            <div>
              <h2 title={computedTitle}>
                {quickMode
                  ? t("quickMode.title", {
                      defaultValue: "Quick truck assignment",
                    })
                  : t("premium.heading", {
                      defaultValue: "Select an Available Truck",
                    })}
              </h2>

              <p>
                {t("premium.subtitle", {
                  defaultValue:
                    "Choose a truck for this transport assignment.",
                })}
              </p>
            </div>

            <button
              type="button"
              className="tsm-premium-close-x"
              disabled={isSavingTruck}
              onClick={onClose}
              aria-label={t("buttons.close", {
                defaultValue: "Close",
              })}
            >
              <X size={21} />
            </button>
          </header>

          {!quickMode && showPrep && (
          <div className="tsm-prepbar" role="status" aria-live="polite">
            <div className="tsm-prep-top">
              <span className={`tsm-prep-chip ${prepStep >= 4 ? "done" : ""}`}>
                {prepStep >= 4
                  ? t("prep.ready", { defaultValue: "Frota pronta" })
                  : t("prep.preparing", { defaultValue: "A preparar frota" })}
              </span>

              <div className="tsm-prep-titlewrap">
                <div className="tsm-prep-title">
                  {prepStep >= 4
                    ? t("prep.sortedTitle", { defaultValue: "RESULTADOS PRONTOS" })
                    : t("prep.sortingTitle", { defaultValue: "A ORDENAR RESULTADOS" })}
                </div>
                <div className="tsm-prep-subtitle">
                  {prepStep >= 4
                    ? t("prep.sortedSub", { defaultValue: "Lista preparada para seleção." })
                    : t("prep.sortingSub", { defaultValue: "A preparar a lista para seleção..." })}
                </div>
              </div>
            </div>

            <div
              className="tsm-prep-track"
              style={{ "--pos": (prepStep / (LOADING_STEPS - 1)) * 100 }}
            >
              <div className="tsm-prep-line" />
              <div className="tsm-prep-truck" aria-hidden="true">
                <Truck size={18} strokeWidth={2.2} />
              </div>

              {Array.from({ length: LOADING_STEPS }).map((_, idx) => {
                const leftPct = (idx / (LOADING_STEPS - 1)) * 100;
                return (
                  <div
                    key={idx}
                    style={{ "--x": leftPct }}
                    className={[
                      "tsm-prep-node",
                      idx < prepStep ? "done" : "",
                      idx === prepStep && prepStep < (LOADING_STEPS - 1) ? "active" : "",
                      idx === (LOADING_STEPS - 1) && prepStep >= (LOADING_STEPS - 1) ? "active done" : "",
                    ].join(" ")}
                    aria-hidden="true"
                  />
                );
              })}
            </div>

            {/* ✅ frases abaixo da estrada (tipo screenshot) */}
            <div className="tsm-prep-below" aria-live="polite">
              <div className="tsm-prep-below-text">
                {prepStep === 0
                  ? t("prep.below.step0", { defaultValue: "A verificar disponibilidade..." })
                  : prepStep === 1
                  ? t("prep.below.step1", { defaultValue: "A carregar frota..." })
                  : prepStep === 2
                  ? t("prep.below.step2", { defaultValue: "A cruzar conflitos..." })
                  : prepStep === 3
                  ? t("prep.below.step3", { defaultValue: "A ordenar resultados..." })
                  : t("prep.below.step4", { defaultValue: "Quase pronto..." })}
              </div>

              <div className="tsm-prep-dots" aria-hidden="true">
                <span className="tsm-prep-dot" />
                <span className="tsm-prep-dot" />
                <span className="tsm-prep-dot" />
              </div>
            </div>
          </div>
        )}

        {/* Info (bulk) - only after loading is done */}
        {mode === "bulk" && !showPrep && (
          <div className="selected-point-simple">
            <div className="point-line point-line--head">
              <span className="point-ico">
                <Navigation size={16} strokeWidth={2.2} />
              </span>
              <span className="point-label">
                {selectedType === "pickup"
                  ? t("bulk.info.pickupsLabel", { count: allPickups.length })
                  : t("bulk.info.deliveriesLabel", { count: allDeliveries.length })}
              </span>
              <span className="point-sep">•</span>
              <span className="point-ico">
                <MapPin size={16} strokeWidth={2.2} />
              </span>
              <strong className="point-strong">
                {(selectedType === "pickup"
                  ? allPickups[0]?.location
                  : allDeliveries[0]?.location) || t("common.unnamed")}
              </strong>
            </div>

            <div className="point-line point-line--meta">
              <span className="point-ico">
                <Calendar size={16} strokeWidth={2.2} />
              </span>
              <span className="point-meta">
                {(selectedType === "pickup"
                  ? allPickups[0]?.date?.replaceAll("-", "/")
                  : allDeliveries[0]?.date?.replaceAll("-", "/")) || t("common.noDate")}
              </span>
              <span className="point-sep">•</span>
              <span className="point-ico">
                <Clock size={16} strokeWidth={2.2} />
              </span>
              <span className="point-meta">
                {(() => {
                  const rawHour =
                    selectedType === "pickup" ? allPickups[0]?.hour : allDeliveries[0]?.hour;
                  if (!rawHour) return t("common.noHour");
                  const [hh, mm] = String(rawHour).split(":");
                  return t("common.hourFmt", { hh, mm });
                })()}
              </span>
            </div>
          </div>
        )}

        {/* Info (single) - only after loading is done */}
        {mode === "single" && selectedPickupDelivery && !showPrep && (
          <div className="selected-point-info">
            <div className="point-line point-line--head">
              <span className="point-ico">
                <Navigation size={16} strokeWidth={2.2} />
              </span>
              <strong className="point-strong">{trPointType(selectedPickupDelivery.type)}</strong>
              <span className="point-sep">•</span>
              <span className="point-ico">
                <MapPin size={16} strokeWidth={2.2} />
              </span>
              <strong className="point-strong">
                {selectedPickupDelivery.location || t("common.unnamed")}
              </strong>
            </div>

            <div className="point-line point-line--meta">
              <span className="point-ico">
                <Calendar size={16} strokeWidth={2.2} />
              </span>
              <span className="point-meta">{selectedPickupDelivery.date || t("common.noDate")}</span>
              <span className="point-sep">•</span>
              <span className="point-ico">
                <Clock size={16} strokeWidth={2.2} />
              </span>
              <span className="point-meta">{selectedPickupDelivery.hour || t("common.noHour")}</span>
            </div>
          </div>
        )}

        {/* Toggle strategic (single) */}
        {mode === "single" && selectedPickupDelivery?.type === "STRATEGICPOINT" && !showPrep && (
          <div className="strategic-toggle">
            <div className="toggle-wrapper">
              <input
                type="checkbox"
                id="strategic-toggle"
                checked={alsoAssignToDeparture}
                onChange={(e) => setAlsoAssignToDeparture(e.target.checked)}
              />
              <label htmlFor="strategic-toggle" className="toggle-label" />
              <span className="toggle-text">
                {selectedPickupDelivery?.truckRole === "arrival"
                  ? t("toggle.assignAlsoDeparture")
                  : selectedPickupDelivery?.truckRole === "departure"
                  ? t("toggle.assignAlsoArrival")
                  : ""}
              </span>
            </div>
          </div>
        )}

        {/* ======== QUICK MODE OR GRID ======== */}
        {quickMode ? (
          <div className="truck-quick-box" onClick={(e) => e.stopPropagation()}>
            <div className="truck-quick-head">
              <div className="truck-quick-head-left">
                <span className="truck-quick-head-icon">
                  <Truck size={20} strokeWidth={2} />
                </span>
                <div className="truck-quick-head-texts">
                  <div className="truck-quick-head-title">
                    {t("quickMode.title", { defaultValue: "Quick truck assignment" })}
                  </div>
                  <div className="truck-quick-head-sub">
                    {t("quickMode.subtitle", {
                      defaultValue: "Carrier is set for this load — skipping availability checks.",
                    })}
                  </div>
                </div>
              </div>
              <span className="truck-quick-head-badge" title="Info">
                i
              </span>
            </div>

            <div className="truck-quick-row">
              <input
                className={`truck-quick-input ${manualPlateError ? "input-error" : ""}`}
                value={manualPlate}
                onChange={(e) => {
                  setManualPlate(e.target.value);
                  if (manualPlateError) setManualPlateError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleQuickAssign();
                }}
                placeholder={t("quickMode.placeholder", {
                  defaultValue: "Type truck plate (e.g. 12-AB-34)",
                })}
              />
            </div>

            {manualPlateError && <div className="truck-quick-error">{manualPlateError}</div>}
          </div>
        ) : (
          <>

            <div className="tsm-premium-toolbar">

              <label className="tsm-premium-search">
                <Search size={18} />
                <input
                  type="search"
                  value={truckSearch}
                  onChange={(event) =>
                    setTruckSearch(event.target.value)
                  }
                  placeholder={t("premium.search", {
                    defaultValue:
                      "Search by plate, brand or model...",
                  })}
                />
              </label>

              <span className="tsm-premium-available-count">
                {loadingTrucks || showPrep
                  ? t("premium.checking", {
                      defaultValue: "Checking...",
                    })
                  : `${selectableTruckCount} ${t(
                      "premium.available",
                      { defaultValue: "available" }
                    )}`}
              </span>

              <div className="tsm-premium-filters">

                <button
                  type="button"
                  className={
                    !onlyAvailableTrucks ? "is-active" : ""
                  }
                  aria-pressed={!onlyAvailableTrucks}
                  onClick={() => setOnlyAvailableTrucks(false)}
                >
                  {t("premium.all", {
                    defaultValue: "All",
                  })}
                </button>

                <button
                  type="button"
                  className={
                    onlyAvailableTrucks ? "is-active" : ""
                  }
                  aria-pressed={onlyAvailableTrucks}
                  onClick={() => setOnlyAvailableTrucks(true)}
                >
                  {t("premium.availableFilter", {
                    defaultValue: "Available",
                  })}
                </button>

              </div>
            </div>

            <div
              className="tsm-premium-columns"
              aria-hidden="true"
            >
              <span>TRUCK</span>
              <span>LOCATION</span>
              <span>STATUS</span>
              <span>SELECT</span>
            </div>

            {(() => {
              const sorted = [...availableTrucks].sort((a, b) => {
                const aAssigned = isCurrentTruck(a);
                const bAssigned = isCurrentTruck(b);

                if (aAssigned !== bAssigned) return aAssigned ? -1 : 1;

                const aBlocked = !!truckBlocking(a)?.isBlocked;
                const bBlocked = !!truckBlocking(b)?.isBlocked;

                if (aBlocked !== bBlocked) return aBlocked ? 1 : -1;

                return String(a.plate || "").localeCompare(String(b.plate || ""));
              });

              const query = truckSearch.trim().toLocaleLowerCase();

              const gridTrucks = sorted.filter((truck) => {
                if (
                  onlyAvailableTrucks &&
                  isTruckUnavailable(truck) &&
                  !isCurrentTruck(truck)
                ) {
                  return false;
                }

                if (!query) return true;

                return [
                  truck.plate,
                  truck.brand,
                  truck.make,
                  truck.model,
                  truck.currentLocation,
                  truck.location,
                ]
                  .filter(Boolean)
                  .join(" ")
                  .toLocaleLowerCase()
                  .includes(query);
              });

              const renderBlockTitle = (block) => {
                if (!block) return "—";

                if (block.blockType === "POINT") {
                  const point = block.points?.[0];

                  return t("blocking.singleAssignment", {
                    type: trPointType(point?.type ?? "?"),
                    location: point?.location ?? "?",
                  });
                }

                if (block.blockType === "LOAD_HALF") {
                  const scope =
                    String(block.points?.[0]?.type || "").toUpperCase() === "PICKUP"
                      ? t("blocking.allPickups")
                      : t("blocking.allDeliveries");

                  return (
                    t("premium.loadOccupation", {
                      defaultValue: "Ocupado noutra carga",
                    }) + " — " + scope
                  );
                }

                if (block.blockType === "LOAD_FULL") {
                  return t("premium.loadOccupation", {
                    defaultValue: "Ocupado noutra carga",
                  });
                }

                return t("premium.sessionOccupation", {
                  defaultValue: "Ocupado noutra sessão de transporte",
                });
              };

              const renderConflictBlock = (block, key) => (
                <div className="tsm-conflict-block" key={key}>
                  {getConflictSessionId(block) != null && (
                    <div className="tsm-conflict-session">
                      <span>SESSÃO DE TRANSPORTE</span>
                      <strong>{getSessionNumberLabel(block)}</strong>
                    </div>
                  )}

                  <div className="tsm-conflict-reason">
                    <Ban size={16} strokeWidth={2.2} />
                    <strong>{renderBlockTitle(block)}</strong>
                  </div>

                  {block?.start && block?.end && (
                    <div className="blocked-interval">
                      <Timer size={16} strokeWidth={2.2} />
                      <span>
                        {fmtDateTime(block.start)}{" "}
                        {t("common.arrow", { defaultValue: "→" })}{" "}
                        {fmtDateTime(block.end)}
                      </span>
                    </div>
                  )}

                  {block?.causedBy?.length > 0 && (
                    <div className="tsm-conflict-causes">
                      <strong>
                        {t("blocking.causedBy", {
                          defaultValue: "Causado por",
                        })}
                      </strong>

                      {block.causedBy.map((cause, index) => (
                        <div className="tsm-conflict-cause" key={index}>
                          <AlertTriangle size={15} />
                          <span>
                            {trPointType(cause.type)} — {cause.location || "—"}
                            {" · "}{cause.date || "—"} {cause.hour || ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {block?.points?.length > 0 && (
                    <ul className="blocked-points-list">
                      {block.points.map((point, index) => (
                        <li key={index}>
                          {trPointType(point.type)} — {point.location || "—"}
                          {" · "}{point.date || "—"} {point.hour || ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );

              return (
                <div className="truck-grid">
                  {gridTrucks.map((truck) => {
                    const conflict = truckBlocking(truck);
                    const alreadyAssigned = isCurrentTruck(truck);
                    const hasConflict = !!conflict?.isBlocked;
                    const isExpanded = expanded.has(truck.id);

                    const isSelected =
                      !alreadyAssigned &&
                      !hasConflict &&
                      selectedTruckId != null &&
                      String(selectedTruckId) === String(truck.id);

                    const statusText = alreadyAssigned
                      ? t("status.alreadyAssigned", {
                          defaultValue: "Já atribuído",
                        })
                      : isSelected
                      ? t("premium.selected", {
                          defaultValue: "Selecionado",
                        })
                      : hasConflict
                      ? t("status.blockedBy", {
                          defaultValue: "Indisponível",
                        })
                      : t("status.available", {
                          defaultValue: "Disponível",
                        });

                    const cannotInteract =
                      isSavingTruck || loadingTrucks || showPrep;

                    const cannotSelect = alreadyAssigned || hasConflict;

                    const handleTruckClick = () => {
                      if (cannotInteract) return;

                      if (hasConflict) {
                        toggleExpand(truck.id, null, conflict);
                        return;
                      }

                      if (alreadyAssigned) return;

                      setSelectedTruckId((previous) =>
                        String(previous) === String(truck.id)
                          ? null
                          : truck.id
                      );

                      setTruckConfirmError("");
                    };

                    return (
                      <div className="truck-grid-item" key={truck.id}>
                        <div
                          className={[
                            "truck-card",
                            hasConflict && !alreadyAssigned ? "blocked" : "",
                            alreadyAssigned ? "tsm-is-assigned" : "",
                            isSelected ? "tsm-is-selected" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          role="button"
                          tabIndex={
                            cannotInteract || (alreadyAssigned && !hasConflict)
                              ? -1
                              : 0
                          }
                          aria-disabled={
                            cannotInteract || (alreadyAssigned && !hasConflict)
                          }
                          aria-expanded={hasConflict ? isExpanded : undefined}
                          aria-pressed={!cannotSelect ? isSelected : undefined}
                          onClick={handleTruckClick}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;

                            if (
                              event.key === "Enter" ||
                              event.key === " "
                            ) {
                              event.preventDefault();
                              handleTruckClick();
                            }
                          }}
                        >
                          <div className="truck-card-top">
                            <div className="truck-left">
                              <span className="truck-icon">
                                <img
                                  src="/icons/truck-optimized.webp"
                                  width={24}
                                  height={18}
                                  alt=""
                                  aria-hidden="true"
                                  draggable={false}
                                  loading="eager"
                                  decoding="async"
                                />
                              </span>

                              <span className="tsm-premium-truck-identity">
                                <strong className="truck-plate">
                                  {truck.plate || "—"}
                                </strong>

                                <small className="tsm-premium-truck-subtitle">
                                  {[truck.brand || truck.make, truck.model]
                                    .filter(Boolean)
                                    .join(" ") || "—"}
                                </small>
                              </span>
                            </div>
                          </div>

                          <div className="tsm-premium-location">
                            <MapPin size={15} aria-hidden="true" />

                            <span>
                              {typeof truck.currentLocation === "string" &&
                              truck.currentLocation.trim()
                                ? truck.currentLocation
                                : typeof truck.location === "string" &&
                                  truck.location.trim()
                                ? truck.location
                                : "—"}
                            </span>
                          </div>

                          <div
                            className={[
                              "truck-status",
                              alreadyAssigned
                                ? "assigned"
                                : isSelected
                                ? "selected"
                                : hasConflict
                                ? "blocked"
                                : "ok",
                            ].join(" ")}
                          >
                            {statusText}
                          </div>

                          <span
                            className="tsm-premium-choice"
                            aria-hidden="true"
                          >
                            {alreadyAssigned || isSelected ? (
                              <CheckCircle2 size={25} />
                            ) : (
                              <span />
                            )}
                          </span>

                          {hasConflict && (
                            <div
                              className={[
                                "block-details",
                                "tsm-conflict-details",
                                isExpanded ? "show" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <div className="tsm-conflict-heading">
                                <span className="tsm-conflict-heading-icon">
                                  <AlertTriangle size={21} />
                                </span>

                                <div className="tsm-conflict-heading-text">
                                  <strong>
                                    Conflito de disponibilidade
                                  </strong>
                                  <small>
                                    Este camião não está disponível neste período.
                                  </small>
                                </div>

                                <span className="tsm-conflict-badge">
                                  Indisponível
                                </span>
                              </div>

                              {mode === "single" &&
                                conflict?.block &&
                                renderConflictBlock(
                                  conflict.block,
                                  "single"
                                )}

                              {mode === "bulk" &&
                                conflict?.blocks?.map((block, index) =>
                                  renderConflictBlock(block, index)
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

          </>
        )}

        {quickMode ? (
          <div className="truck-modal-actions">
            <button onClick={onClose} className="truck-modal-btn truck-modal-btn--secondary">
              {t("buttons.close")}
            </button>
            <button onClick={handleQuickAssign} className="truck-modal-btn truck-modal-btn--primary">
              {t("buttons.assign", { defaultValue: "Assign" })}
            </button>
          </div>
        ) : (
          <div className="tsm-premium-footer">
            {truckConfirmError && (
              <small
                className="tsm-premium-error"
                role="alert"
              >
                {truckConfirmError}
              </small>
            )}

            <div className="tsm-premium-footer-buttons">
              <button
                type="button"
                className="truck-modal-btn truck-modal-btn--secondary"
                onClick={onClose}
                disabled={isSavingTruck}
              >
                {t("buttons.close", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                className="truck-modal-btn truck-modal-btn--primary"
                onClick={confirmTruckSelection}
                disabled={!selectedTruck || isSavingTruck || loadingTrucks || showPrep || isTruckUnavailable(selectedTruck)}
              >
                {isSavingTruck
                  ? "Selecting..."
                  : "Select truck"}
                <ArrowRight size={17} />
              </button>
            </div>
          </div>
        )}
        </section>
      </div>
    </div>
  );
}

export default TruckSelectorModal;